/**
 * Faction & Ideology Types
 *
 * Data model for philosophical faction systems with reputation tracking,
 * ideology compass, and NPC affiliations. Factions represent competing
 * worldviews, not just political organizations.
 */
import { z } from 'zod';
// =============================================================================
// IDEOLOGY AXES
// =============================================================================
/**
 * Two-axis ideology compass. Each axis ranges from -100 to +100.
 *
 * Axis 1: Order <-> Entropy
 *   Negative = Order (structure, law, permanence)
 *   Positive = Entropy (change, chaos, dissolution)
 *
 * Axis 2: Self <-> Collective
 *   Negative = Self (individual will, personal transcendence)
 *   Positive = Collective (community, shared fate, duty)
 */
export const IdeologyAxesSchema = z.object({
    orderEntropy: z.number().min(-100).max(100).default(0),
    selfCollective: z.number().min(-100).max(100).default(0),
});
export const IDEOLOGY_QUADRANT_LABELS = {
    architect: {
        label: 'Architect',
        description: 'Personal mastery through structure. Shape yourself, shape the world.',
    },
    harmonist: {
        label: 'Harmonist',
        description: 'Society must be perfected. Order serves the many.',
    },
    liberator: {
        label: 'Liberator',
        description: 'Pure freedom. Break all chains, even your own.',
    },
    dissolver: {
        label: 'Dissolver',
        description: 'Tear it all down for rebirth. Endings are beginnings.',
    },
};
/** Compute quadrant from ideology axes */
export function getIdeologyQuadrant(axes) {
    if (axes.orderEntropy <= 0 && axes.selfCollective <= 0)
        return 'architect';
    if (axes.orderEntropy <= 0 && axes.selfCollective > 0)
        return 'harmonist';
    if (axes.orderEntropy > 0 && axes.selfCollective <= 0)
        return 'liberator';
    return 'dissolver';
}
// =============================================================================
// FACTION
// =============================================================================
export const FactionRoleSchema = z.enum(['leader', 'lieutenant', 'agent', 'sympathizer']);
export const ReputationTierSchema = z.enum(['hostile', 'unfriendly', 'neutral', 'friendly', 'allied']);
export const ReputationThresholdsSchema = z.object({
    hostile: z.number().default(-60),
    unfriendly: z.number().default(-20),
    // neutral is implicit (between unfriendly and friendly)
    friendly: z.number().default(20),
    allied: z.number().default(60),
});
/** Get the reputation tier from a numeric score */
export function getReputationTier(score, thresholds) {
    if (score <= thresholds.hostile)
        return 'hostile';
    if (score <= thresholds.unfriendly)
        return 'unfriendly';
    if (score >= thresholds.allied)
        return 'allied';
    if (score >= thresholds.friendly)
        return 'friendly';
    return 'neutral';
}
export const FactionSchema = z.object({
    id: z.string(),
    name: z.string(),
    philosophy: z.string(), // One-line credo
    description: z.string(), // 2-3 sentence description
    ideologyPosition: IdeologyAxesSchema, // Where they sit on the compass
    color: z.string(), // Hex color for UI
    iconName: z.string(), // Icon identifier
    isSecret: z.boolean().default(false), // Hidden from players initially
    headquarters: z.string().nullable(), // District ID
    reputationThresholds: ReputationThresholdsSchema,
});
// =============================================================================
// FACTION NPC
// =============================================================================
export const FactionNpcSchema = z.object({
    id: z.string(),
    name: z.string(),
    title: z.string().optional(), // "Forge Warden", "The Moth"
    factionId: z.string(),
    role: FactionRoleSchema,
    districtId: z.string(),
    description: z.string(), // 2-sentence personality sketch
    disposition: z.string(), // How they treat party based on reputation
    dialogueHints: z.array(z.string()), // 3-5 phrases the AI can riff on
    isSecret: z.boolean().default(false), // GM-only until revealed
});
// =============================================================================
// FACTION REPUTATION
// =============================================================================
export const ReputationEventSchema = z.object({
    event: z.string(),
    delta: z.number(),
    timestamp: z.string(), // ISO date
});
export const FactionReputationSchema = z.object({
    id: z.string().optional(),
    campaignId: z.string(),
    factionId: z.string(),
    characterId: z.string().default('party'), // Character ID or 'party'
    reputationScore: z.number().min(-100).max(100).default(0),
    keyEvents: z.array(ReputationEventSchema).default([]),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
// =============================================================================
// FACTION QUEST HOOKS
// =============================================================================
export const FactionQuestHookSchema = z.object({
    id: z.string(),
    factionId: z.string(),
    title: z.string(),
    description: z.string(),
    reputationRequired: z.number().default(0), // Min rep to see this hook
    conflictsWith: z.array(z.string()).default([]), // Faction IDs angered by accepting
    rewardReputation: z.number().default(0), // Rep gained on completion
    ideologyShift: IdeologyAxesSchema.optional(), // How this shifts character ideology
});
// =============================================================================
// CHARACTER IDEOLOGY
// =============================================================================
export const IdeologyShiftEventSchema = z.object({
    action: z.string(),
    shift: IdeologyAxesSchema,
    timestamp: z.string(),
});
export const CharacterIdeologySchema = z.object({
    id: z.string().optional(),
    campaignId: z.string(),
    characterId: z.string(),
    axes: IdeologyAxesSchema,
    history: z.array(IdeologyShiftEventSchema).default([]),
    dominantLabel: z.string().optional(), // Computed from quadrant
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
//# sourceMappingURL=faction-types.js.map